<template>
  <el-dialog
    :visible.sync="dialog"
    :title="title"
    :show-close="!forced"
    :close-on-click-modal="false"
    :close-on-press-escape="!forced"
    :before-close="handleBeforeClose"
    append-to-body
    width="560px"
    @close="handleClose"
  >
    <p v-if="forced" class="pwd-expired-tip">您的密码已长期未修改，为保障账号安全，请及时更新密码。</p>
    <el-form ref="form" :model="form" :rules="rules" size="small" label-width="100px">
      <el-form-item label="新密码" prop="newPass">
        <el-input
          v-model="form.newPass"
          type="password"
          auto-complete="new-password"
          show-password
          placeholder="密码必须包含大小写字母、数字、特殊字符,且不少于8位"
          style="width: 400px;"
        />
        <div class="pwd-special-tip">请勿设置连续相同字符或顺序数字，特殊字符支持：._~!@#$^&amp;*</div>
      </el-form-item>
      <el-form-item label="确认密码" prop="confirmPass">
        <el-input
          v-model="form.confirmPass"
          type="password"
          auto-complete="new-password"
          show-password
          placeholder="请再次输入新密码"
          style="width: 400px;"
        />
      </el-form-item>
      <el-form-item v-if="maskedPhone" label="手机号码">
        <span class="phone-text">{{ maskedPhone }}</span>
      </el-form-item>
      <el-form-item label="验证码" prop="verCode">
        <el-input
          v-model="form.verCode"
          maxlength="6"
          placeholder="请输入6位数字"
          style="width: 400px;"
          @keyup.enter.native="doSubmit"
        >
          <template slot="append">
            <el-button
              type="text"
              size="medium"
              style="width:80px;color:#146ef7"
              :disabled="smsCount > 0 || smsSending"
              @click="sendCode"
            >{{ smsCount > 0 ? smsCount + 's后重发' : '发送验证码' }}</el-button>
          </template>
        </el-input>
      </el-form-item>
    </el-form>
    <div slot="footer" class="dialog-footer">
      <el-button v-if="forced" type="warning" @click="handleLogout">退出登录</el-button>
      <el-button v-if="!forced" type="text" @click="dialog = false">取消</el-button>
      <el-button :loading="loading" type="primary" @click="doSubmit">确认</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { encrypt } from '@/utils/rsaEncrypt'
import { resetPwdBySms, sendResetPwdSms } from '@/api/system/user'

export default {
  name: 'UpdatePass',
  props: {
    // 手机号，缺省取当前登录用户手机号
    phone: {
      type: String,
      default: ''
    },
    // 用户id，缺省取当前登录用户id
    userId: {
      type: [String, Number],
      default: ''
    },
    // 密码已过期时的强制修改：隐藏取消与关闭按钮，不允许关闭弹窗
    forced: {
      type: Boolean,
      default: false
    },
    // 支持 :visible.sync 控制弹窗
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const confirmPass = (rule, value, callback) => {
      if (value) {
        if (this.form.newPass !== value) {
          callback(new Error('两次输入的密码不一致'))
        } else {
          callback()
        }
      } else {
        callback(new Error('请再次输入密码'))
      }
    }
    return {
      loading: false,
      // 验证码发送中
      smsSending: false,
      dialog: false,
      title: '修改登录密码',
      form: { newPass: '', confirmPass: '', verCode: '' },
      rules: {
        newPass: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { required: true, pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[._~!@#$^&*])[a-zA-Z0-9._~!@#$^&*]{8,30}$/, message: '密码必须包含大小写字母、数字、特殊字符,且不少于8位', trigger: 'blur' }
        ],
        confirmPass: [
          { required: true, validator: confirmPass, trigger: 'blur' }
        ],
        verCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { min: 6, max: 6, message: '请输入6位数字', trigger: 'blur' }
        ]
      },
      // 验证码倒计时
      smsCount: 0,
      smsTimer: null
    }
  },
  computed: {
    ...mapGetters(['user']),
    // 脱敏后的手机号
    maskedPhone() {
      const phone = String(this.phone || (this.user && this.user.phone) || '')
      if (phone.length === 11) {
        return phone.substring(0, 3) + '****' + phone.substring(7)
      }
      return phone
    },
    currentUserId() {
      return this.userId || (this.user && this.user.id) || ''
    }
  },
  watch: {
    visible(val) {
      this.dialog = val
    },
    dialog(val) {
      if (val !== this.visible) {
        this.$emit('update:visible', val)
      }
    }
  },
  beforeDestroy() {
    this.stopCountdown()
  },
  methods: {
    // 打开弹窗，已打开时不重复打开
    open() {
      if (this.dialog) return
      this.dialog = true
    },
    // 非强制模式下允许关闭，强制模式忽略关闭操作
    handleBeforeClose(done) {
      if (this.forced) return
      done()
    },
    // 密码过期强制修改时退出登录
    handleLogout() {
      this.$store.dispatch('LogOut').then(() => {
        location.reload()
      })
    },
    handleClose() {
      this.stopCountdown()
      this.smsCount = 0
      this.resetForm()
    },
    // 发送短信验证码
    sendCode() {
      this.smsSending = true
      sendResetPwdSms().then(() => {
        this.$message.success('验证码已发送，请注意查收')
        this.startCountdown()
        this.smsSending = false
      }).catch(() => {
        this.smsSending = false
      })
    },
    startCountdown() {
      this.stopCountdown()
      const TIME_COUNT = 60
      this.smsCount = TIME_COUNT
      this.smsTimer = setInterval(() => {
        this.smsCount--
        if (this.smsCount <= 0) {
          this.stopCountdown()
        }
      }, 1000)
    },
    stopCountdown() {
      if (this.smsTimer) {
        clearInterval(this.smsTimer)
        this.smsTimer = null
      }
    },
    doSubmit() {
      if (this.loading) return
      this.$refs['form'].validate((valid) => {
        if (!valid) return
        this.loading = true
        resetPwdBySms({
          newPass: encrypt(this.form.newPass),
          userId: this.currentUserId,
          verCode: this.form.verCode
        }).then(() => {
          this.loading = false
          this.dialog = false
          this.$notify({
            title: '密码修改成功，请重新登录',
            type: 'success',
            duration: 1500
          })
          setTimeout(() => {
            this.$store.dispatch('LogOut').then(() => {
              location.reload() // 为了重新实例化vue-router对象 避免bug
            })
          }, 1500)
        }).catch(() => {
          this.loading = false
        })
      })
    },
    resetForm() {
      this.form = { newPass: '', confirmPass: '', verCode: '' }
      if (this.$refs['form']) {
        this.$refs['form'].clearValidate()
      }
    }
  }
}
</script>

<style scoped>
.phone-text {
  color: #606266;
}
.pwd-special-tip {
  width: 400px;
  margin-top: 4px;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
  text-align: right;
}

.pwd-expired-tip {
  margin: 0 0 16px;
  padding: 8px 12px;
  color: #e6a23c;
  background: #fdf6ec;
  border-radius: 4px;
  font-size: 13px;
  line-height: 20px;
}
</style>
